import {
  ARTICLE_CATEGORY_LABELS,
  ARTICLE_STATUS_LABELS,
  type ArticleCategory,
  type ArticleStatus,
  articleCategorySchema,
  articleStatusSchema,
  type ListArticlesQuery,
} from '@idpe/schema';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';

import { Badge } from '~/components/ui/Badge.js';
import { Button } from '~/components/ui/Button.js';
import {
  DataTable,
  PaginationControls,
  type SortState,
  useDataTableSort,
} from '~/components/ui/DataTable.js';
import { Field } from '~/components/ui/Field.js';
import { Select } from '~/components/ui/Select.js';
import { useArticlesList } from '~/features/articles/hooks.js';

const STATUS_TONE: Record<ArticleStatus, 'primary' | 'success' | 'warning' | 'neutral' | 'muted'> =
  {
    rascunho: 'neutral',
    em_revisao: 'warning',
    aprovado: 'primary',
    publicado: 'success',
    arquivado: 'muted',
  };

const searchSchema = z.object({
  q: z.string().optional(),
  categoria: articleCategorySchema.optional(),
  status: articleStatusSchema.optional(),
  destaque: z
    .string()
    .optional()
    .transform((v) => (v === 'true' ? true : v === 'false' ? false : undefined)),
  page: z.coerce.number().int().min(1).default(1).optional(),
  perPage: z.coerce.number().int().min(1).max(100).default(20).optional(),
  sort: z.string().optional(),
});

export const Route = createFileRoute('/_auth/articles/')({
  validateSearch: (search) => searchSchema.parse(search),
  component: ArticlesListPage,
});

function ArticlesListPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [qInput, setQInput] = useState(search.q ?? '');
  const [sort, setSort] = useDataTableSort(
    search.sort
      ? (() => {
          const [campo, dir] = search.sort.split(':');
          return campo
            ? ({ campo, dir: (dir === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc' } as SortState)
            : undefined;
        })()
      : undefined,
  );

  const query: Partial<ListArticlesQuery> = {
    q: search.q,
    categoria: search.categoria,
    status: search.status,
    destaque: search.destaque,
    page: search.page ?? 1,
    perPage: search.perPage ?? 20,
    sort: sort ? `${sort.campo}:${sort.dir}` : undefined,
  };

  const { data, isLoading, error, refetch } = useArticlesList(query);

  function updateSearch(patch: Partial<typeof search>) {
    navigate({
      to: '/articles',
      search: { ...search, ...patch, page: patch.page ?? 1 },
    });
  }

  return (
    <div className="page">
      <header className="page-head">
        <p className="eyebrow">/ Blog</p>
        <h1 className="page-title">Artigos</h1>
        <p className="page-sub">
          Rascunhos, artigos no ar e arquivados. Só os publicados aparecem para quem visita o site.
          Rascunhos ficam guardados aqui pelo tempo que precisar; excluir de vez é permitido apenas
          a administradores.
        </p>
      </header>

      <section>
        <div className="toolbar">
          <div className="toolbar__row">
            <div className="toolbar__filters">
              <Field
                label="Buscar"
                placeholder="título ou resumo"
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') updateSearch({ q: qInput || undefined });
                }}
                onBlur={() => updateSearch({ q: qInput || undefined })}
              />
              <Select
                label="Categoria"
                value={search.categoria ?? ''}
                onChange={(e) =>
                  updateSearch({
                    categoria: (e.target.value as ArticleCategory) || undefined,
                  })
                }
                options={[
                  { value: '', label: 'Todas' },
                  ...Object.entries(ARTICLE_CATEGORY_LABELS).map(([v, l]) => ({
                    value: v,
                    label: l,
                  })),
                ]}
              />
              <Select
                label="Status"
                value={search.status ?? ''}
                onChange={(e) =>
                  updateSearch({ status: (e.target.value as ArticleStatus) || undefined })
                }
                options={[
                  { value: '', label: 'Todos' },
                  ...Object.entries(ARTICLE_STATUS_LABELS).map(([v, l]) => ({
                    value: v,
                    label: l,
                  })),
                ]}
              />
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <Link to="/articles/new" style={{ width: '100%' }}>
                  <Button variant="primary" fullWidth>
                    Novo artigo
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 'var(--s-5)' }}>
          <DataTable
            data={data?.data ?? []}
            loading={isLoading}
            error={error ? { message: error.displayMessage, retry: () => void refetch() } : null}
            keyExtractor={(a) => a.id}
            sort={sort}
            onSortChange={(s) => {
              setSort(s);
              updateSearch({ sort: s ? `${s.campo}:${s.dir}` : undefined });
            }}
            onRowClick={(a) => navigate({ to: '/articles/$slug', params: { slug: a.slug } })}
            emptyMessage="Nenhum artigo encontrado com esses filtros."
            emptyAction={
              <Link to="/articles/new">
                <Button variant="secondary">Criar primeiro artigo</Button>
              </Link>
            }
            columns={[
              {
                key: 'titulo',
                header: 'Título',
                sortable: true,
                mobileLine: 'primary',
                cell: (a) => (
                  <span>
                    <strong>{a.titulo}</strong>
                    {a.destaque && (
                      <Badge tone="primary" className="dt-card__meta-item">
                        destaque
                      </Badge>
                    )}
                  </span>
                ),
              },
              {
                key: 'categoria',
                header: 'Categoria',
                mobileLine: 'secondary',
                cell: (a) => ARTICLE_CATEGORY_LABELS[a.categoriaSlug],
              },
              {
                key: 'status',
                header: 'Status',
                mobileLine: 'meta',
                cell: (a) => (
                  <Badge tone={STATUS_TONE[a.status]}>{ARTICLE_STATUS_LABELS[a.status]}</Badge>
                ),
              },
              {
                key: 'publicadoEm',
                header: 'Publicado em',
                sortable: true,
                hideOnMobile: true,
                mobileLine: 'meta',
                cell: (a) =>
                  a.publicadoEm ? new Date(a.publicadoEm).toLocaleDateString('pt-BR') : '—',
              },
              {
                key: 'tempo',
                header: 'Leitura',
                align: 'right',
                hideOnMobile: true,
                mobileLine: 'hidden',
                cell: (a) => `${a.tempoLeituraMin} min`,
              },
            ]}
          />
        </div>

        {data && data.total > 0 && (
          <PaginationControls
            page={data.page}
            perPage={data.perPage}
            total={data.total}
            totalPages={data.totalPages}
            onPageChange={(page) => updateSearch({ page })}
          />
        )}
      </section>
    </div>
  );
}
